import type { AdController } from "../types";
import Hls from "hls.js";

interface VastMediaFile {
  url: string;
  type: string;
  width: number;
  height: number;
  bitrate?: number | undefined;
}

interface VastTrackingUrls {
  impression: string[];
  start: string[];
  firstQuartile: string[];
  midpoint: string[];
  thirdQuartile: string[];
  complete: string[];
  mute: string[];
  unmute: string[];
  pause: string[];
  resume: string[];
  fullscreen: string[];
  exitFullscreen: string[];
  skip: string[];
  error: string[];
}

interface VastAd {
  id: string;
  title: string;
  duration: number;
  mediaFiles: VastMediaFile[];
  trackingUrls: VastTrackingUrls;
  clickThrough?: string | undefined;
}

type VastParseResult =
  | { type: "inline"; ad: VastAd }
  | {
      type: "wrapper";
      vastAdTagUri: string;
      trackingUrls: VastTrackingUrls;
      clickThrough?: string | undefined;
    }
  | { type: "empty" };

const MAX_VAST_WRAPPER_DEPTH = 5;

export function createHlsAdPlayer(
  contentVideo: HTMLVideoElement,
  options?: {
    continueLiveStreamDuringAds?: boolean;
    licenseKey?: string;
    mainHlsInstance?: Hls;
  }
): AdController {
  let adPlaying = false;
  let originalMutedState = false;
  let originalVolume = Math.max(0, Math.min(1, contentVideo.volume || 1));
  const listeners = new Map<string, Set<(payload?: any) => void>>();
  const licenseKey = options?.licenseKey;
  const mainHlsInstance = options?.mainHlsInstance;

  let adVideoElement: HTMLVideoElement | undefined;
  let adHls: Hls | undefined;
  let adContainerEl: HTMLDivElement | undefined;
  let currentAd: VastAd | undefined;
  let podAds: VastAd[] = [];
  let podIndex = 0;
  let sessionId: string | undefined;
  const preloadedAds = new Map<string, VastAd>();
  const preloadingAds = new Map<string, Promise<void>>();
  let destroyed = false;
  let pendingTimeouts: number[] = [];

  const STALL_TIMEOUT_MS = 4000;
  const STALL_CHECK_INTERVAL_MS = 1000;
  let stallWatchdogId: number | undefined;
  let lastAdProgressTime = 0;
  let lastAdProgressPosition = 0;

  let trackingFired = {
    impression: false,
    start: false,
    firstQuartile: false,
    midpoint: false,
    thirdQuartile: false,
    complete: false,
  };

  function emit(event: string, payload?: any): void {
    const set = listeners.get(event);
    if (!set) return;
    for (const fn of Array.from(set)) {
      try {
        fn(payload);
      } catch (error) {
        console.warn(
          `[HlsAdPlayer] Error in event listener for ${event}:`,
          error
        );
      }
    }
  }

  function generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  function fireTrackingPixels(urls: string[]): void {
    if (!urls || urls.length === 0) return;

    urls.forEach((url) => {
      try {
        let trackingUrl = url;

        if (sessionId) {
          trackingUrl = `${trackingUrl}${
            trackingUrl.includes("?") ? "&" : "?"
          }session_id=${sessionId}`;
        }

        if (licenseKey) {
          trackingUrl = `${trackingUrl}${
            trackingUrl.includes("?") ? "&" : "?"
          }license_key=${licenseKey}`;
        }

        const img = new Image(1, 1);
        img.src = trackingUrl;
        console.log(`[HlsAdPlayer] Fired tracking pixel: ${trackingUrl}`);
      } catch (error) {
        console.warn(`[HlsAdPlayer] Error firing tracking pixel:`, error);
      }
    });
  }

  function getMainStreamQuality(): {
    width: number;
    height: number;
    bitrate: number;
  } | null {
    if (!mainHlsInstance || !mainHlsInstance.levels) {
      return null;
    }

    const currentLevel = mainHlsInstance.currentLevel;
    if (currentLevel === -1 || !mainHlsInstance.levels[currentLevel]) {
      const autoLevel = mainHlsInstance.loadLevel;
      if (autoLevel !== -1 && mainHlsInstance.levels[autoLevel]) {
        const level = mainHlsInstance.levels[autoLevel];
        return {
          width: level.width || 1920,
          height: level.height || 1080,
          bitrate: level.bitrate || 5000000,
        };
      }
      return null;
    }

    const level = mainHlsInstance.levels[currentLevel];
    return {
      width: level.width || 1920,
      height: level.height || 1080,
      bitrate: level.bitrate || 5000000,
    };
  }

  function selectBestMediaFile(mediaFiles: VastMediaFile[]): VastMediaFile {
    if (mediaFiles.length === 0) {
      throw new Error("No media files available");
    }

    const firstFile = mediaFiles[0];
    if (!firstFile) {
      throw new Error("No media files available");
    }

    if (mediaFiles.length === 1) {
      return firstFile;
    }

    const mainQuality = getMainStreamQuality();
    if (!mainQuality) {
      console.log(
        "[HlsAdPlayer] No main stream quality info, using first media file"
      );
      return firstFile;
    }

    console.log("[HlsAdPlayer] Main stream quality:", mainQuality);

    const scoredFiles = mediaFiles.map((file) => {
      const widthDiff = Math.abs(file.width - mainQuality.width);
      const heightDiff = Math.abs(file.height - mainQuality.height);
      const resolutionDiff = widthDiff + heightDiff;

      const fileBitrate = (file.bitrate || 5000) * 1000;
      const bitrateDiff = Math.abs(fileBitrate - mainQuality.bitrate);

      const score = resolutionDiff * 2 + bitrateDiff / 1000;

      return { file, score, resolutionDiff, bitrateDiff };
    });

    scoredFiles.sort((a, b) => a.score - b.score);

    const bestMatch = scoredFiles[0];
    if (!bestMatch) {
      console.log("[HlsAdPlayer] No best match found, using first media file");
      return firstFile;
    }

    console.log("[HlsAdPlayer] Selected media file:", {
      url: bestMatch.file.url,
      resolution: `${bestMatch.file.width}x${bestMatch.file.height}`,
      bitrate: bestMatch.file.bitrate,
      score: bestMatch.score,
      resolutionDiff: bestMatch.resolutionDiff,
      bitrateDiff: bestMatch.bitrateDiff,
    });

    return bestMatch.file;
  }

  function isHlsMediaFile(mediaFile: VastMediaFile): boolean {
    const type = mediaFile.type.toLowerCase();
    const url = mediaFile.url.toLowerCase();
    return (
      type === "application/x-mpegurl" ||
      type === "application/vnd.apple.mpegurl" ||
      type.includes("mpegurl") ||
      url.includes(".m3u8")
    );
  }

  function isProgressiveMediaFile(mediaFile: VastMediaFile): boolean {
    const type = mediaFile.type.toLowerCase();
    const url = mediaFile.url.toLowerCase();
    return (
      type.startsWith("video/") ||
      url.endsWith(".mp4") ||
      url.includes(".mp4?")
    );
  }

  function createEmptyTrackingUrls(): VastTrackingUrls {
    return {
      impression: [],
      start: [],
      firstQuartile: [],
      midpoint: [],
      thirdQuartile: [],
      complete: [],
      mute: [],
      unmute: [],
      pause: [],
      resume: [],
      fullscreen: [],
      exitFullscreen: [],
      skip: [],
      error: [],
    };
  }

  function extractTrackingUrls(scope: Document | Element): VastTrackingUrls {
    const trackingUrls = createEmptyTrackingUrls();

    scope.querySelectorAll("Impression").forEach((el) => {
      const url = el.textContent?.trim();
      if (url) trackingUrls.impression.push(url);
    });

    scope.querySelectorAll("Tracking").forEach((el) => {
      const event = el.getAttribute("event");
      const url = el.textContent?.trim();
      if (event && url) {
        const eventKey = event as keyof VastTrackingUrls;
        if (trackingUrls[eventKey]) {
          trackingUrls[eventKey].push(url);
        }
      }
    });

    scope.querySelectorAll("Error").forEach((el) => {
      const url = el.textContent?.trim();
      if (url) trackingUrls.error.push(url);
    });

    return trackingUrls;
  }

  function mergeTrackingUrls(
    target: VastTrackingUrls,
    source: VastTrackingUrls
  ): VastTrackingUrls {
    (Object.keys(target) as Array<keyof VastTrackingUrls>).forEach((key) => {
      if (source[key] && source[key].length > 0) {
        target[key] = target[key].concat(source[key]);
      }
    });
    return target;
  }

  function parseVastXml(xmlString: string): VastParseResult {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, "text/xml");

      const parserError = xmlDoc.querySelector("parsererror");
      if (parserError) {
        console.error(
          "[HlsAdPlayer] XML parsing error (malformed VAST XML):",
          parserError.textContent
        );
        return { type: "empty" };
      }

      const adElement = xmlDoc.querySelector("Ad");
      if (!adElement) {
        console.warn("[HlsAdPlayer] No Ad element found in VAST XML");
        return { type: "empty" };
      }

      const adId = adElement.getAttribute("id") || "unknown";
      const title = xmlDoc.querySelector("AdTitle")?.textContent || "Ad";

      const clickThrough = xmlDoc
        .querySelector("ClickThrough")
        ?.textContent?.trim();

      const wrapperUri = xmlDoc
        .querySelector("VASTAdTagURI")
        ?.textContent?.trim();
      if (wrapperUri) {
        console.log(
          `[HlsAdPlayer] VAST wrapper detected, following VASTAdTagURI: ${wrapperUri}`
        );
        return {
          type: "wrapper",
          vastAdTagUri: wrapperUri,
          trackingUrls: extractTrackingUrls(xmlDoc),
          clickThrough,
        };
      }

      const isNoAdAvailable =
        adId === "empty" ||
        title.toLowerCase().includes("no ad available") ||
        title.toLowerCase() === "no ad available";

      const durationText =
        xmlDoc.querySelector("Duration")?.textContent || "00:00:30";
      const durationParts = durationText.split(":");
      const duration =
        parseInt(durationParts[0] || "0", 10) * 3600 +
        parseInt(durationParts[1] || "0", 10) * 60 +
        parseInt(durationParts[2] || "0", 10);

      const mediaFileElements = xmlDoc.querySelectorAll("MediaFile");
      const mediaFiles: VastMediaFile[] = [];

      console.log(
        `[HlsAdPlayer] Found ${mediaFileElements.length} MediaFile element(s) in VAST XML`
      );

      mediaFileElements.forEach((mf, index) => {
        const type = mf.getAttribute("type") || "";
        const url = mf.textContent?.trim() || "";
        const width = mf.getAttribute("width") || "";
        const height = mf.getAttribute("height") || "";

        console.log(
          `[HlsAdPlayer] MediaFile ${index}: type="${type}", url="${url}", width="${width}", height="${height}"`
        );

        const mediaFile: VastMediaFile = {
          url,
          type,
          width: parseInt(width || "1920", 10),
          height: parseInt(height || "1080", 10),
          bitrate: undefined,
        };

        if (!url) {
          console.warn(`[HlsAdPlayer] MediaFile ${index} has empty URL`);
          return;
        }

        const bitrateAttr = mf.getAttribute("bitrate");
        const bitrateValue = bitrateAttr
          ? parseInt(bitrateAttr, 10)
          : undefined;
        mediaFile.bitrate =
          bitrateValue && bitrateValue > 0 ? bitrateValue : undefined;

        if (isHlsMediaFile(mediaFile) || isProgressiveMediaFile(mediaFile)) {
          mediaFiles.push(mediaFile);
          console.log(
            `[HlsAdPlayer] Added ${isHlsMediaFile(mediaFile) ? "HLS" : "progressive"} MediaFile: ${url}`
          );
        } else {
          console.log(
            `[HlsAdPlayer] MediaFile ${index} ignored (type="${type}" is not supported)`
          );
        }
      });

      if (mediaFiles.length === 0) {
        if (isNoAdAvailable) {
          console.warn(
            "[HlsAdPlayer] No ads available (VAST response indicates no ads)"
          );
        } else {
          console.warn("[HlsAdPlayer] No supported media files found in VAST XML");
        }
        return { type: "empty" };
      }

      return {
        type: "inline",
        ad: {
          id: adId,
          title,
          duration,
          mediaFiles,
          trackingUrls: extractTrackingUrls(xmlDoc),
          clickThrough,
        },
      };
    } catch (error) {
      console.error("[HlsAdPlayer] Error parsing VAST XML:", error);
      return { type: "empty" };
    }
  }

  function parseAdElement(adElement: Element): VastParseResult {
    try {
      const adId = adElement.getAttribute("id") || "unknown";
      const title = adElement.querySelector("AdTitle")?.textContent || "Ad";

      const clickThrough = adElement
        .querySelector("ClickThrough")
        ?.textContent?.trim();

      const wrapperUri = adElement
        .querySelector("VASTAdTagURI")
        ?.textContent?.trim();
      if (wrapperUri) {
        return {
          type: "wrapper",
          vastAdTagUri: wrapperUri,
          trackingUrls: extractTrackingUrls(adElement),
          clickThrough,
        };
      }

      const isNoAdAvailable =
        adId === "empty" ||
        title.toLowerCase().includes("no ad available") ||
        title.toLowerCase() === "no ad available";

      const durationText =
        adElement.querySelector("Duration")?.textContent || "00:00:30";
      const durationParts = durationText.split(":");
      const duration =
        parseInt(durationParts[0] || "0", 10) * 3600 +
        parseInt(durationParts[1] || "0", 10) * 60 +
        parseInt(durationParts[2] || "0", 10);

      const mediaFileElements = adElement.querySelectorAll("MediaFile");
      const mediaFiles: VastMediaFile[] = [];

      mediaFileElements.forEach((mf) => {
        const type = mf.getAttribute("type") || "";
        const url = mf.textContent?.trim() || "";
        const width = mf.getAttribute("width") || "";
        const height = mf.getAttribute("height") || "";

        if (!url) return;

        const bitrateAttr = mf.getAttribute("bitrate");
        const bitrateValue = bitrateAttr ? parseInt(bitrateAttr, 10) : undefined;

        const mediaFile: VastMediaFile = {
          url,
          type,
          width: parseInt(width || "1920", 10),
          height: parseInt(height || "1080", 10),
          bitrate: bitrateValue && bitrateValue > 0 ? bitrateValue : undefined,
        };

        if (isHlsMediaFile(mediaFile) || isProgressiveMediaFile(mediaFile)) {
          mediaFiles.push(mediaFile);
        }
      });

      if (mediaFiles.length === 0) {
        if (isNoAdAvailable) {
          console.warn(
            "[HlsAdPlayer] Pod <Ad> indicates no ad available"
          );
        } else {
          console.warn("[HlsAdPlayer] No supported media files in pod <Ad>");
        }
        return { type: "empty" };
      }

      return {
        type: "inline",
        ad: {
          id: adId,
          title,
          duration,
          mediaFiles,
          trackingUrls: extractTrackingUrls(adElement),
          clickThrough,
        },
      };
    } catch (error) {
      console.error("[HlsAdPlayer] Error parsing pod <Ad> element:", error);
      return { type: "empty" };
    }
  }

  async function fetchVastXml(
    vastTagUrl: string,
    credentials: RequestCredentials = "include"
  ): Promise<string> {
    const requestVast = (creds: RequestCredentials) =>
      fetch(vastTagUrl, {
        mode: "cors",
        credentials: creds,
        headers: {
          "Accept": "application/xml, text/xml, */*",
        },
        referrerPolicy: "no-referrer-when-downgrade",
      });

    let response: Response;
    try {
      response = await requestVast(credentials);
    } catch (error) {
      if (credentials !== "omit") {
        console.warn(
          `[HlsAdPlayer] VAST fetch failed with credentials="${credentials}", retrying without credentials:`,
          error
        );
        response = await requestVast("omit");
      } else {
        throw error;
      }
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch VAST: ${response.statusText}`);
    }
    return response.text();
  }

  async function fetchAndParseVastAd(
    vastTagUrl: string,
    depth = 0,
    accumulatedTracking: VastTrackingUrls = createEmptyTrackingUrls()
  ): Promise<VastAd | null> {
    const vastXml = await fetchVastXml(
      vastTagUrl,
      depth === 0 ? "include" : "omit"
    );
    console.log("[HlsAdPlayer] VAST XML received");
    console.log(
      "[HlsAdPlayer] VAST XML content (first 2000 chars):",
      vastXml.substring(0, 2000)
    );

    const parsed = parseVastXml(vastXml);

    if (parsed.type === "empty") {
      return null;
    }

    if (parsed.type === "wrapper") {
      if (depth >= MAX_VAST_WRAPPER_DEPTH) {
        console.warn(
          `[HlsAdPlayer] VAST wrapper depth limit (${MAX_VAST_WRAPPER_DEPTH}) reached, aborting redirect chain`
        );
        return null;
      }

      mergeTrackingUrls(accumulatedTracking, parsed.trackingUrls);
      return fetchAndParseVastAd(
        parsed.vastAdTagUri,
        depth + 1,
        accumulatedTracking
      );
    }

    mergeTrackingUrls(parsed.ad.trackingUrls, accumulatedTracking);
    return parsed.ad;
  }

  async function fetchAndParsePod(vastTagUrl: string): Promise<VastAd[]> {
    const vastXml = await fetchVastXml(vastTagUrl);
    console.log("[HlsAdPlayer] Pod VAST XML received");

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(vastXml, "text/xml");

    if (xmlDoc.querySelector("parsererror")) {
      console.error("[HlsAdPlayer] Pod VAST XML parsing error (malformed)");
      return [];
    }

    const adElements = Array.from(xmlDoc.querySelectorAll("Ad"));
    if (adElements.length === 0) {
      console.warn("[HlsAdPlayer] No <Ad> elements found in pod VAST response");
      return [];
    }

    adElements.sort((a, b) => {
      const aSeq = parseInt(a.getAttribute("sequence") || "", 10);
      const bSeq = parseInt(b.getAttribute("sequence") || "", 10);
      const aVal = Number.isFinite(aSeq) ? aSeq : Number.MAX_SAFE_INTEGER;
      const bVal = Number.isFinite(bSeq) ? bSeq : Number.MAX_SAFE_INTEGER;
      return aVal - bVal;
    });

    const ads: VastAd[] = [];
    for (const adElement of adElements) {
      const parsed = parseAdElement(adElement);

      if (parsed.type === "inline") {
        ads.push(parsed.ad);
      } else if (parsed.type === "wrapper") {
        try {
          const resolved = await fetchAndParseVastAd(
            parsed.vastAdTagUri,
            1,
            parsed.trackingUrls
          );
          if (resolved) {
            ads.push(resolved);
          }
        } catch (error) {
          console.warn(
            "[HlsAdPlayer] Failed to resolve wrapper <Ad> in pod:",
            error
          );
        }
      }
    }

    console.log(`[HlsAdPlayer] Parsed ${ads.length} ad(s) from pod VAST response`);
    return ads;
  }

  function createAdVideoElement(): HTMLVideoElement {
    const video = document.createElement("video");
    video.style.position = "absolute";
    video.style.left = "0";
    video.style.top = "0";
    video.style.width = "100%";
    video.style.height = "100%";
    video.style.objectFit = "contain";
    video.style.backgroundColor = "#000";
    video.playsInline = true;
    video.muted = false;

    video.volume = 1.0;
    console.log(
      `[HlsAdPlayer] Created ad video element with volume ${video.volume}`
    );

    return video;
  }

  function setupAdEventListeners(): void {
    if (!adVideoElement || !currentAd) return;

    adVideoElement.addEventListener("timeupdate", () => {
      if (!currentAd || !adVideoElement) return;

      noteAdProgress();

      const progress = adVideoElement.currentTime / currentAd.duration;

      if (progress >= 0.25 && !trackingFired.firstQuartile) {
        trackingFired.firstQuartile = true;
        fireTrackingPixels(currentAd.trackingUrls.firstQuartile);
      }

      if (progress >= 0.5 && !trackingFired.midpoint) {
        trackingFired.midpoint = true;
        fireTrackingPixels(currentAd.trackingUrls.midpoint);
      }

      if (progress >= 0.75 && !trackingFired.thirdQuartile) {
        trackingFired.thirdQuartile = true;
        fireTrackingPixels(currentAd.trackingUrls.thirdQuartile);
      }
    });

    adVideoElement.addEventListener("playing", () => {
      startStallWatchdog();
      if (!currentAd || trackingFired.start) return;
      trackingFired.start = true;
      fireTrackingPixels(currentAd.trackingUrls.start);
      console.log("[HlsAdPlayer] Ad started playing");
    });

    adVideoElement.addEventListener("ended", () => {
      if (!currentAd || trackingFired.complete) return;
      trackingFired.complete = true;
      fireTrackingPixels(currentAd.trackingUrls.complete);
      console.log("[HlsAdPlayer] Ad completed");

      if (podIndex < podAds.length - 1 && advanceToNextPodAd()) {
        return;
      }

      handleAdComplete();
    });

    adVideoElement.addEventListener("error", (e) => {
      if (!adPlaying) return;
      console.error("[HlsAdPlayer] Ad video error:", e);
      if (currentAd) {
        fireTrackingPixels(currentAd.trackingUrls.error);
      }

      if (podIndex < podAds.length - 1 && advanceToNextPodAd()) {
        return;
      }

      handleAdError();
    });

    adVideoElement.addEventListener("volumechange", () => {
      if (!currentAd) return;
      if (adVideoElement!.muted) {
        fireTrackingPixels(currentAd.trackingUrls.mute);
      } else {
        fireTrackingPixels(currentAd.trackingUrls.unmute);
      }
    });

    adVideoElement.addEventListener("pause", () => {
      if (currentAd && !adVideoElement!.ended) {
        fireTrackingPixels(currentAd.trackingUrls.pause);
      }
    });

    adVideoElement.addEventListener("play", () => {
      if (currentAd && adVideoElement!.currentTime > 0) {
        fireTrackingPixels(currentAd.trackingUrls.resume);
      }
    });
  }

  function setAdPlayingFlag(isPlaying: boolean): void {
    if (isPlaying) {
      contentVideo.dataset.stormcloudAdPlaying = "true";
    } else {
      delete contentVideo.dataset.stormcloudAdPlaying;
    }
  }

  function resetQuartileTracking(preserveImpression: boolean): void {
    trackingFired = {
      impression: preserveImpression ? trackingFired.impression : false,
      start: false,
      firstQuartile: false,
      midpoint: false,
      thirdQuartile: false,
      complete: false,
    };
  }

  function loadCurrentAdMedia(): void {
    if (!adVideoElement || !currentAd) {
      throw new Error("No ad video element or ad to load");
    }

    const mediaFile = selectBestMediaFile(currentAd.mediaFiles);
    if (!mediaFile) {
      throw new Error("No media file available for ad");
    }

    const isHlsAd = isHlsMediaFile(mediaFile);
    console.log(
      `[HlsAdPlayer] Loading ${isHlsAd ? "HLS" : "progressive"} ad (pod ${podIndex + 1}/${podAds.length || 1}) from: ${mediaFile.url}`
    );

    if (isHlsAd && Hls.isSupported()) {
      if (adHls) {
        adHls.destroy();
      }

      adHls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
      });

      adHls.loadSource(mediaFile.url);
      adHls.attachMedia(adVideoElement);

      adHls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log("[HlsAdPlayer] HLS manifest parsed, starting playback");
        adVideoElement!.play().catch((error) => {
          console.error("[HlsAdPlayer] Error starting ad playback:", error);
          handleAdError();
        });
      });

      adHls.on(Hls.Events.ERROR, (_event, data) => {
        console.error("[HlsAdPlayer] HLS error:", data);
        if (data.fatal) {
          handleAdError();
        }
      });
    } else if (
      isHlsAd &&
      adVideoElement.canPlayType("application/vnd.apple.mpegurl")
    ) {
      adVideoElement.src = mediaFile.url;
      adVideoElement.play().catch((error) => {
        console.error("[HlsAdPlayer] Error starting ad playback:", error);
        handleAdError();
      });
    } else if (!isHlsAd && isProgressiveMediaFile(mediaFile)) {
      if (adHls) {
        adHls.destroy();
        adHls = undefined;
      }

      adVideoElement.src = mediaFile.url;
      adVideoElement.load();
      adVideoElement.play().catch((error) => {
        console.error("[HlsAdPlayer] Error starting progressive ad playback:", error);
        handleAdError();
      });
    } else {
      throw new Error(`Unsupported ad media file type: ${mediaFile.type}`);
    }
  }

  function advanceToNextPodAd(): boolean {
    if (podIndex >= podAds.length - 1) {
      return false;
    }

    podIndex++;
    currentAd = podAds[podIndex];
    resetQuartileTracking(false);

    if (currentAd) {
      fireTrackingPixels(currentAd.trackingUrls.impression);
      trackingFired.impression = true;
      console.log(
        `[HlsAdPlayer] Advancing to next pod ad (${podIndex + 1}/${podAds.length}): ${currentAd.title}, duration: ${currentAd.duration}s`
      );
    }

    try {
      loadCurrentAdMedia();
      emit("pod_ad_started", { index: podIndex, total: podAds.length });
      return true;
    } catch (error) {
      console.error("[HlsAdPlayer] Failed to load next pod ad:", error);
      return false;
    }
  }

  function clearStallWatchdog(): void {
    if (stallWatchdogId != null) {
      clearInterval(stallWatchdogId);
      stallWatchdogId = undefined;
    }
  }

  function noteAdProgress(): void {
    lastAdProgressTime = Date.now();
    if (adVideoElement) {
      lastAdProgressPosition = adVideoElement.currentTime;
    }
  }

  function handleAdStall(): void {
    console.warn(
      "[HlsAdPlayer] Ad playback stalled (no progress) - recovering to avoid a blank frame"
    );
    clearStallWatchdog();

    if (currentAd) {
      fireTrackingPixels(currentAd.trackingUrls.error);
    }

    if (podIndex < podAds.length - 1 && advanceToNextPodAd()) {
      return;
    }

    handleAdError();
  }

  function startStallWatchdog(): void {
    clearStallWatchdog();
    noteAdProgress();
    stallWatchdogId = window.setInterval(() => {
      if (!adPlaying || !adVideoElement) {
        return;
      }

      if (adVideoElement.currentTime > lastAdProgressPosition + 0.05) {
        noteAdProgress();
        return;
      }

      if (adVideoElement.paused) {
        noteAdProgress();
        return;
      }

      if (Date.now() - lastAdProgressTime >= STALL_TIMEOUT_MS) {
        handleAdStall();
      }
    }, STALL_CHECK_INTERVAL_MS) as unknown as number;
  }

  function handleAdComplete(): void {
    console.log("[HlsAdPlayer] Handling ad completion");
    clearStallWatchdog();
    adPlaying = false;
    setAdPlayingFlag(false);

    contentVideo.muted = true;

    if (adContainerEl) {
      adContainerEl.style.display = "none";
      adContainerEl.style.pointerEvents = "none";
    }

    if (!options?.continueLiveStreamDuringAds) {
      contentVideo.style.visibility = "visible";
      contentVideo.style.opacity = "1";
    }
    
    if (options?.continueLiveStreamDuringAds) {
      if (contentVideo.paused) {
        console.log("[HlsAdPlayer] Content video paused in live mode, resuming playback");
        contentVideo.play().catch(() => {});
      } else {
        console.log("[HlsAdPlayer] Content video already playing in live mode");
      }
    }

    emit("content_resume");
  }

  function handleAdError(): void {
    console.log("[HlsAdPlayer] Handling ad error");
    clearStallWatchdog();
    adPlaying = false;
    setAdPlayingFlag(false);

    contentVideo.muted = true;

    if (adContainerEl) {
      adContainerEl.style.display = "none";
      adContainerEl.style.pointerEvents = "none";
    }

    // Never leave a black overlay covering the content after a failed ad.
    if (!options?.continueLiveStreamDuringAds) {
      contentVideo.style.visibility = "visible";
      contentVideo.style.opacity = "1";
    }

    emit("ad_error");
  }

  return {
    initialize() {
      console.log("[HlsAdPlayer] Initializing");

      if (!adContainerEl) {
        const container = document.createElement("div");
        container.style.position = "absolute";
        container.style.left = "0";
        container.style.top = "0";
        container.style.right = "0";
        container.style.bottom = "0";
        container.style.display = "none";
        container.style.alignItems = "center";
        container.style.justifyContent = "center";
        container.style.pointerEvents = "none";
        container.style.zIndex = "30";
        container.style.backgroundColor = "#000";

        contentVideo.parentElement?.appendChild(container);
        adContainerEl = container;
      }
    },

    async requestAds(vastTagUrl: string) {
      console.log("[HlsAdPlayer] Requesting ads:", vastTagUrl);

      if (adPlaying) {
        console.warn(
          "[HlsAdPlayer] Cannot request new ads while an ad is playing"
        );
        return Promise.reject(new Error("Ad already playing"));
      }

      try {
        sessionId = generateSessionId();
        let ads: VastAd[] = [];

        if (preloadedAds.has(vastTagUrl)) {
          const preloaded = preloadedAds.get(vastTagUrl);
          preloadedAds.delete(vastTagUrl);
          if (preloaded) {
            ads = [preloaded];
          }
          console.log(
            "[HlsAdPlayer] Using preloaded VAST response:",
            vastTagUrl
          );
        } else {
          ads = await fetchAndParsePod(vastTagUrl);
        }

        if (!ads.length) {
          console.warn("[HlsAdPlayer] No ads available from VAST response");
          emit("ad_error");
          return Promise.reject(new Error("No ads available from VAST response"));
        }

        podAds = ads;
        podIndex = 0;
        currentAd = ads[0];
        console.log(
          `[HlsAdPlayer] Pod parsed: ${ads.length} ad(s), first="${currentAd?.title}", duration=${currentAd?.duration}s`
        );

        if (currentAd) {
          fireTrackingPixels(currentAd.trackingUrls.impression);
          trackingFired.impression = true;
        }

        return Promise.resolve();
      } catch (error) {
        console.error("[HlsAdPlayer] Error requesting ads:", error);
        emit("ad_error");
        return Promise.reject(error);
      }
    },
    async play() {
      if (!currentAd) {
        console.warn(
          "[HlsAdPlayer] Cannot play: No ad loaded (no ads available)"
        );
        return Promise.reject(new Error("No ad loaded"));
      }

      console.log("[HlsAdPlayer] Starting ad playback");

      try {
        if (!adVideoElement) {
          adVideoElement = createAdVideoElement();
          adContainerEl?.appendChild(adVideoElement);
          setupAdEventListeners();
        }

        resetQuartileTracking(true);

        const contentVolume = contentVideo.volume;
        originalVolume = Math.max(
          0,
          Math.min(1, contentVolume || originalVolume)
        );

        if (!options?.continueLiveStreamDuringAds) {
          contentVideo.pause();
          console.log("[HlsAdPlayer] Content paused (VOD mode)");
        } else {
          console.log("[HlsAdPlayer] Content continues (Live mode)");
        }

        console.log("[HlsAdPlayer] FORCE MUTING main video");
        contentVideo.muted = true;
        adPlaying = true;
        setAdPlayingFlag(true);

        if (adVideoElement) {
          adVideoElement.volume = Math.max(0, Math.min(1, originalVolume));
          adVideoElement.muted = originalMutedState;
          console.log(
            `[HlsAdPlayer] Set ad video volume to ${adVideoElement.volume}, muted: ${adVideoElement.muted}, originalMutedState: ${originalMutedState}, contentVolume: ${contentVolume}`
          );
        }

        if (adContainerEl) {
          adContainerEl.style.display = "flex";
          adContainerEl.style.pointerEvents = "auto";
        }

        emit("content_pause");

        loadCurrentAdMedia();

        return Promise.resolve();
      } catch (error) {
        console.error("[HlsAdPlayer] Error playing ad:", error);
        handleAdError();
        return Promise.reject(error);
      }
    },
    pause() {
      if (!adPlaying || !adVideoElement) {
        return;
      }

      try {
        if (adVideoElement && !adVideoElement.paused) {
          adVideoElement.pause();
        }
      } catch (error) {
        console.warn("[HlsAdPlayer] Error pausing ad:", error);
      }
    },
    resume() {
      if (!adPlaying || !adVideoElement) {
        return;
      }

      try {
        if (adVideoElement && adVideoElement.paused) {
          adVideoElement.play().catch(() => {});
        }
      } catch (error) {
        console.warn("[HlsAdPlayer] Error resuming ad:", error);
      }
    },
    async stop() {
      console.log("[HlsAdPlayer] Stopping ad");
      clearStallWatchdog();
      adPlaying = false;
      setAdPlayingFlag(false);

      const previousMutedState = contentVideo.muted;
      contentVideo.muted = originalMutedState;
      contentVideo.volume = Math.max(0, Math.min(1, originalVolume));
      console.log(
        `[HlsAdPlayer] Restored mute state on stop: ${previousMutedState} -> ${originalMutedState}, volume: ${contentVideo.volume}`
      );

      if (adContainerEl) {
        adContainerEl.style.display = "none";
        adContainerEl.style.pointerEvents = "none";
      }

      contentVideo.style.visibility = "visible";
      contentVideo.style.opacity = "1";

      if (options?.continueLiveStreamDuringAds) {
        if (contentVideo.paused) {
          console.log("[HlsAdPlayer] Content video paused in live mode, resuming playback on stop");
          contentVideo.play().catch(() => {});
        }
      }

      if (adHls) {
        adHls.destroy();
        adHls = undefined;
      }

      if (adVideoElement) {
        adVideoElement.pause();
        adVideoElement.removeAttribute("src");
        try {
          adVideoElement.load();
        } catch {
          /* no-op: load() can throw on some platforms after src removal */
        }
      }

      currentAd = undefined;
      podAds = [];
      podIndex = 0;
    },

    destroy() {
      console.log("[HlsAdPlayer] Destroying");
      destroyed = true;
      clearStallWatchdog();

      for (const timeoutId of pendingTimeouts) {
        clearTimeout(timeoutId);
      }
      pendingTimeouts = [];
      
      adPlaying = false;
      setAdPlayingFlag(false);
      contentVideo.muted = originalMutedState;
      contentVideo.volume = Math.max(0, Math.min(1, originalVolume));

      if (adHls) {
        adHls.destroy();
        adHls = undefined;
      }

      if (adVideoElement) {
        adVideoElement.pause();
        adVideoElement.removeAttribute("src");
        try {
          adVideoElement.load();
        } catch {
          /* no-op: load() can throw on some platforms after src removal */
        }
        adVideoElement.remove();
        adVideoElement = undefined;
      }

      if (adContainerEl?.parentElement) {
        adContainerEl.parentElement.removeChild(adContainerEl);
      }

      adContainerEl = undefined;
      currentAd = undefined;
      podAds = [];
      podIndex = 0;
      listeners.clear();
      preloadedAds.clear();
      preloadingAds.clear();
    },

    isAdPlaying() {
      return adPlaying;
    },

    resize(width: number, height: number) {
      console.log(`[HlsAdPlayer] Resizing to ${width}x${height}`);

      if (adContainerEl) {
        adContainerEl.style.width = `${width}px`;
        adContainerEl.style.height = `${height}px`;
      }

      if (adVideoElement) {
        adVideoElement.style.width = `${width}px`;
        adVideoElement.style.height = `${height}px`;
      }
    },

    on(event: string, listener: (payload?: any) => void) {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event)!.add(listener);
    },

    off(event: string, listener: (payload?: any) => void) {
      listeners.get(event)?.delete(listener);
    },

    updateOriginalMutedState(muted: boolean, volume?: number) {
      const nextVolume =
        typeof volume === "number" && !Number.isNaN(volume)
          ? Math.max(0, Math.min(1, volume))
          : originalVolume;
      console.log(
        `[HlsAdPlayer] updateOriginalMutedState called: { muted: ${originalMutedState} -> ${muted}, volume: ${originalVolume} -> ${nextVolume} }`
      );
      originalMutedState = muted;
      originalVolume = nextVolume;
    },

    getOriginalMutedState() {
      return originalMutedState;
    },
    getOriginalVolume() {
      return originalVolume;
    },

    setAdVolume(volume: number) {
      if (adVideoElement && adPlaying) {
        const clamped = Math.max(0, Math.min(1, volume));
        adVideoElement.volume = clamped;
        adVideoElement.muted = clamped === 0;
      }
    },

    getAdVolume(): number {
      if (adVideoElement && adPlaying) {
        return adVideoElement.volume;
      }
      return 1;
    },
    showPlaceholder() {
      contentVideo.style.opacity = "0";
      contentVideo.style.visibility = "hidden";
      
      if (!adContainerEl) {
        const container = document.createElement("div");
        container.style.position = "absolute";
        container.style.left = "0";
        container.style.top = "0";
        container.style.right = "0";
        container.style.bottom = "0";
        container.style.display = "none";
        container.style.alignItems = "center";
        container.style.justifyContent = "center";
        container.style.pointerEvents = "none";
        container.style.zIndex = "10";
        container.style.backgroundColor = "#000";

        contentVideo.parentElement?.appendChild(container);
        adContainerEl = container;
      }

      if (adContainerEl) {
        adContainerEl.style.display = "flex";
        adContainerEl.style.pointerEvents = "auto";
      }
    },
    hidePlaceholder() {
      if (adContainerEl) {
        adContainerEl.style.display = "none";
        adContainerEl.style.pointerEvents = "none";
      }
      
      if (!adPlaying) {
        contentVideo.style.visibility = "visible";
        contentVideo.style.opacity = "1";
      }
    },
    getLoadedAdDuration() {
      return currentAd?.duration;
    },
    getLoadedAdId() {
      return currentAd?.id;
    },
    getPodAdCount() {
      return podAds.length;
    },
    getPodTotalDurationMs() {
      return podAds.reduce(
        (total, ad) => total + (ad.duration > 0 ? ad.duration * 1000 : 0),
        0
      );
    },
  };
}
